import React, { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import {
  applyTrackingOptOutFromUrl,
  getAnalyticsPageContext,
  trackActiveTime,
  trackPageView,
} from '../../services/siteAnalytics';

const ACTIVE_TIME_FLUSH_INTERVAL_MS = 15000;
// Первая фиксация времени — рано: многие посетители (особенно из webview
// VK) закрывают вкладку до 15-й секунды, и pagehide-beacon не всегда
// успевает — у таких визитов время вообще не фиксировалось.
const ACTIVE_TIME_FIRST_FLUSH_MS = 5000;

const SiteAnalyticsTracker: React.FC = () => {
  const location = useLocation();
  const lastTrackedPathRef = useRef('');
  const currentPagePathRef = useRef('');
  const activeStartedAtRef = useRef<number | null>(null);
  const accumulatedSecondsRef = useRef(0);

  const stopActiveTimer = useCallback(() => {
    if (typeof document === 'undefined' || activeStartedAtRef.current === null) {
      return;
    }

    const elapsedMs = Date.now() - activeStartedAtRef.current;
    activeStartedAtRef.current = null;

    if (elapsedMs <= 0) {
      return;
    }

    accumulatedSecondsRef.current += elapsedMs / 1000;
  }, []);

  const startActiveTimer = useCallback(() => {
    if (typeof document === 'undefined' || document.hidden || activeStartedAtRef.current !== null) {
      return;
    }

    activeStartedAtRef.current = Date.now();
  }, []);

  const flushActiveTime = useCallback(() => {
    stopActiveTimer();

    const currentPagePath = currentPagePathRef.current;
    const durationSeconds = Math.round(accumulatedSecondsRef.current);

    if (currentPagePath && durationSeconds > 0) {
      trackActiveTime({
        pagePath: currentPagePath,
        durationSeconds,
      });
    }

    accumulatedSecondsRef.current = 0;
  }, [stopActiveTimer]);

  useEffect(() => {
    applyTrackingOptOutFromUrl();
  }, []);

  useEffect(() => {
    const rawPagePath = `${location.pathname}${location.search || ''}`;
    const pageContext = getAnalyticsPageContext(rawPagePath);
    if (pageContext.pagePath === lastTrackedPathRef.current) {
      return;
    }

    if (currentPagePathRef.current) {
      flushActiveTime();
    }

    lastTrackedPathRef.current = pageContext.pagePath;
    currentPagePathRef.current = pageContext.pagePath;
    trackPageView(rawPagePath);
    startActiveTimer();

    const firstFlushId = window.setTimeout(() => {
      if (!document.hidden && currentPagePathRef.current) {
        flushActiveTime();
        startActiveTimer();
      }
    }, ACTIVE_TIME_FIRST_FLUSH_MS);

    return () => {
      window.clearTimeout(firstFlushId);
    };
  }, [flushActiveTime, location.pathname, location.search, startActiveTimer]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      if (typeof document === 'undefined' || document.hidden || !currentPagePathRef.current) {
        return;
      }

      flushActiveTime();
      startActiveTimer();
    }, ACTIVE_TIME_FLUSH_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [flushActiveTime, startActiveTimer]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        flushActiveTime();
        return;
      }

      startActiveTimer();
    };

    const handlePageHide = () => {
      flushActiveTime();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('beforeunload', handlePageHide);

    startActiveTimer();

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('beforeunload', handlePageHide);
      flushActiveTime();
    };
  }, [flushActiveTime, startActiveTimer]);

  return null;
};

export default SiteAnalyticsTracker;
